import { getDocumentTypeLabel } from './documentTypes';
import { formatEntryGuideItemProject } from './entryGuideItemProject';
import { formatPrintDate } from './dateUtils';
import { sortGuideItemsAlphabetically } from './sortPrintLineItems';

export const GUIDE_TYPES_WITHOUT_PROJECT = new Set(['entrada', 'traspaso', 'recepcion', 'ajuste']);

export const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

export const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

export const fmtSignedQty = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });
};

export function parseAdjustmentDeltaFromDescription(description) {
  if (!description) return null;
  const match = String(description).match(/\(Δ\s*([-\d.,]+)\)/);
  if (!match) return null;
  const n = parseFloat(match[1].replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function getGuideItemAdjustmentDelta(item) {
  if (item?.adjustment_delta !== undefined && item?.adjustment_delta !== null && item?.adjustment_delta !== '') {
    const n = parseFloat(item.adjustment_delta);
    if (Number.isFinite(n)) return n;
  }

  const movements = item?.kardex_movements;
  const list = Array.isArray(movements) ? movements : movements ? [movements] : [];
  if (list.length > 0) {
    const deltaBase = list.reduce((sum, mov) => {
      const qty = Math.abs(parseFloat(mov.quantity) || 0);
      return sum + (mov.movement_type === 'salida' ? -qty : qty);
    }, 0);
    const rate = parseFloat(item?.conversion_rate) || 1;

    return deltaBase / (rate > 0 ? rate : 1);
  }

  const parsed = parseAdjustmentDeltaFromDescription(item?.description);
  if (parsed !== null) {
    const rate = parseFloat(item?.conversion_rate) || 1;

    return parsed / (rate > 0 ? rate : 1);
  }

  return null;
}

export function itemUsesReferenceDocumentPricing(item) {
  return (
    item?.reference_unit_price !== undefined
    && item?.reference_unit_price !== null
    && item?.reference_unit_price !== ''
  );
}

export function guideUsesReferenceDocumentPricing(guide) {
  return (guide?.items || []).some(itemUsesReferenceDocumentPricing);
}

export function getGuideItemDocumentUnitPrice(item) {
  if (itemUsesReferenceDocumentPricing(item)) {
    return parseFloat(item.reference_unit_price);
  }
  return parseFloat(item?.unit_price || 0);
}

export function getGuideItemLineSubtotal(guide, item) {
  if (guide?.guide_type === 'ajuste') {
    const delta = getGuideItemAdjustmentDelta(item);
    const lineTotal = Math.abs(parseFloat(item.total ?? item.subtotal ?? 0));

    if (delta !== null && Number.isFinite(delta)) {
      if (delta === 0) return 0;
      return Math.sign(delta) * lineTotal;
    }

    return parseFloat(item.total ?? item.subtotal ?? 0);
  }

  if (itemUsesReferenceDocumentPricing(item)) {
    const qty = parseFloat(item.quantity || 0);
    const unitPrice = getGuideItemDocumentUnitPrice(item);
    return Math.round(qty * unitPrice * 100) / 100;
  }

  if (item.subtotal != null) return parseFloat(item.subtotal);

  return getGuideItemDocumentUnitPrice(item) * parseFloat(item.quantity || 0);
}

export function getGuideItemSubtotalDisplay(guide, item) {
  const currencySymbol = getGuideCurrencySymbol(guide);
  return `${currencySymbol} ${fmtMoney(getGuideItemLineSubtotal(guide, item))}`;
}

export function getGuideItemQuantityDisplay(guide, item) {
  if (guide?.guide_type === 'ajuste') {
    const delta = getGuideItemAdjustmentDelta(item);
    if (delta !== null) return fmtSignedQty(delta);
  }
  return fmtQty(item?.quantity);
}

/** Fecha calendario (sin hora) alineada con listado de almacén y kardex (America/Lima). */
export function formatGuideDate(value) {
  return formatPrintDate(value) || '';
}

export function getGuideTitle(guide) {
  const type = guide?.guide_type;
  if (type === 'ajuste') return 'GUÍA DE AJUSTE DE INVENTARIO';
  if (type === 'salida') return 'GUÍA DE SALIDA';
  if (type === 'inicial') return 'GUÍA DE INVENTARIO INICIAL';
  if (type === 'traspaso') return 'GUÍA DE TRASPASO';
  if (type === 'recepcion') return 'GUÍA DE RECEPCIÓN';
  return 'GUÍA DE ENTRADA';
}

export function getGuideStatusInfo(guide) {
  const map = {
    completed: { label: 'Completado', tone: 'completed' },
    draft: { label: 'Borrador', tone: 'open' },
    cancelled: { label: 'Cancelado', tone: 'cancelled' },
    pending: { label: 'Pendiente', tone: 'open' },
    issued: { label: 'Emitido', tone: 'issued' },
  };
  return map[guide?.status] || { label: guide?.status || '—', tone: 'issued' };
}

export function getGuideCurrencySymbol(guide) {
  if (guide?.guide_type === 'ajuste') return 'S/';
  return guide?.currency === 'USD' ? '$' : 'S/';
}

export function formatWarehouseRef(warehouse) {
  if (!warehouse) return null;
  const name = warehouse.name?.trim();
  const code = warehouse.code?.trim();
  if (name) return code ? `${name} (${code})` : name;
  return code || null;
}

export function docTypeLabel(supplier) {
  if (supplier?.document_type === 'ruc') return 'RUC';
  if (supplier?.document_type === 'dni') return 'DNI';
  if (supplier?.document_type) return String(supplier.document_type).toUpperCase();
  return 'Documento';
}

export function getItemDescription(item) {
  return item?.product?.name || item?.description || 'N/A';
}

export function getItemProject(item) {
  return formatEntryGuideItemProject(item);
}

export function shouldHideProjectColumn(guide) {
  return GUIDE_TYPES_WITHOUT_PROJECT.has(guide?.guide_type);
}

export function collectGuideRequirements(guide) {
  const byKey = new Map();

  const addRequirement = (req) => {
    if (!req?.code) return;
    const key = req.id ?? req.code;
    if (!byKey.has(key)) {
      byKey.set(key, req);
    }
  };

  addRequirement(guide?.requirement);

  (guide?.items || []).forEach((item) => {
    addRequirement(item?.requirement_item?.requirement);
    addRequirement(item?.requirement);
  });

  return Array.from(byKey.values());
}

export function buildGuideReferenceRows(guide) {
  const rows = [];

  collectGuideRequirements(guide).forEach((req) => {
    const detail = [
      req.supplier?.name,
      req.request_date ? formatGuideDate(req.request_date) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push({ type: 'Requerimiento', code: req.code || '—', detail: detail || '—' });
  });

  if (guide?.document) {
    const docRef = guide.document;
    const number =
      docRef.series && docRef.number
        ? `${docRef.series}-${docRef.number}`
        : docRef.code || '—';
    const detail = [
      getDocumentTypeLabel(docRef.document_type),
      docRef.issue_date ? formatGuideDate(docRef.issue_date) : null,
      docRef.supplier?.name,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push({ type: 'Documento', code: number, detail: detail || '—' });
  }

  if (guide?.purchase_order) {
    const po = guide.purchase_order;
    const detail = [
      po.supplier?.name,
      po.order_date ? formatGuideDate(po.order_date) : null,
      po.total_amount != null ? `Total: S/ ${fmtMoney(po.total_amount)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    rows.push({ type: 'Orden de compra', code: po.code || '—', detail: detail || '—' });
  }

  if (guide?.transfer_guide) {
    rows.push({
      type: 'Guía de traspaso',
      code: guide.transfer_guide.code || '—',
      detail: '—',
    });
  }

  return rows;
}

export function getGuideTableColumns(guide) {
  if (guide?.guide_type === 'ajuste') {
    return ['Descripción', 'Unidad', 'Cant.', 'P. Unit', 'Subtotal'];
  }
  if (shouldHideProjectColumn(guide)) {
    return ['Descripción', 'Unidad', 'Cant.', 'P. Unit', 'Subtotal'];
  }
  return ['Descripción', 'Unidad', 'Proyecto', 'Cant.', 'P. Unit', 'Subtotal'];
}

export function getSortedGuideItems(guide) {
  return sortGuideItemsAlphabetically(guide?.items);
}

export function buildGuideItemRows(guide) {
  const currencySymbol = getGuideCurrencySymbol(guide);
  const hideProject = shouldHideProjectColumn(guide);

  return getSortedGuideItems(guide).map((item) => {
    const row = {
      description: getItemDescription(item),
      unit: item.unit_of_measure || item.product?.unit_of_measure || 'UN',
      quantity: getGuideItemQuantityDisplay(guide, item),
      unitPrice: `${currencySymbol} ${fmtUnitPrice(getGuideItemDocumentUnitPrice(item))}`,
      subtotal: getGuideItemSubtotalDisplay(guide, item),
    };

    if (!hideProject) {
      row.project = getItemProject(item);
    }

    return row;
  });
}

export function getGuideTotalAmount(guide) {
  if (guide?.guide_type === 'ajuste' || guideUsesReferenceDocumentPricing(guide)) {
    return (guide?.items || []).reduce(
      (sum, item) => sum + getGuideItemLineSubtotal(guide, item),
      0,
    );
  }

  return parseFloat(guide?.total_amount || 0);
}

export function getGuideTotalDisplay(guide) {
  const currencySymbol = getGuideCurrencySymbol(guide);
  return `${currencySymbol} ${fmtMoney(getGuideTotalAmount(guide))}`;
}

export function shouldShowGuideTotal(guide) {
  if (guide?.guide_type === 'ajuste') {
    return (guide?.items || []).length > 0;
  }

  return true;
}
